import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  getBaseInfo,
  getInterviewSchedules,
  getCandidateFeedbacks,
  getHmExperienceSurveys,
} from 'src/action-creators/crm';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {usePageTitle} from 'src/contexts/page-title-context';
import {useInflight} from 'src/hooks/redux';

import {useHistory} from 'src/rerouter/index';
import {useSelector, useDispatch} from 'react-redux';
import {UpcomingInterview} from 'src/components/crm/jobs/job-info/overview/upcoming-interviews/upcoming-interview';
import {HiringPipelinePerformanceChart} from 'src/components/crm/dashboard/hiring-pipeline-performance-chart/hiring-pipeline-performance-chart';
import {
  selectCandidateInterviewSchedules,
  selectCandidateHmExperienceSurveys,
  selectCandidateFeedbacks,
  selectCompletedInterviewSchedules,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Feedbacks} from 'src/components/crm/jobs/job-info/overview/feedbacks/feedbacks';
import {Tasks} from 'src/components/crm/jobs/job-info/overview/tasks/tasks';
import {Referrals} from 'src/components/crm/dashboard/referrals/referrals';
import {HelpfulLinks} from 'src/components/crm/dashboard/helpful-links/helpful-links';
import PendingApprovals from 'src/components/crm/dashboard/approvals/pending-approvals-card';
import {KPIBox} from '@spaced-out/ui-design-system/lib/components/KPIBox';
import {useApi} from 'src/hooks/useApi';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {duotoneIconStyle} from 'src/utils/app-shell';
import {selectJobFilters} from 'src/selectors/analytics/analytics-hiring-pipeline';
import {
  getDateRangeDataByOption,
  secondsToTimeBreakdown,
} from 'src/utils/analytics-genesis';
import {getSavedDashboards} from 'src/action-creators/analytics/analytics-hiring-pipeline';
import {selectAgencyData} from 'src/selectors/agency';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import css from './dashboard.css';


export const Dashboard = () => {
  const {t, language} = useLabelTranslation();
  const userInfo = useSelector(selectUserInfo);

  const GLOBAL_DASHBOARD_STATS = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_GLOBAL_DASHBOARD_STATS,
  );

  const ALL_TASK_STATS = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_GLOBAL_CANDIDATE_DASHBOARD_STATS,
  );

  const DOWNLOAD_SURVEY_RESPONSE = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.HIRING_MANAGER_SURVEY,
  );

  const INTERNAL_JOB_POSTING = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_IJP_REQUISITIONS,
  );

  const VIEW_RESUME_MAILBOX = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_RESUME_MAILBOX,
  );

  const ADD_REFERRALS = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.ADD_REFERRALS,
  );

  const SHARE_REFERRAL_LINK = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.SHARE_REFERRAL_LINK,
  );
  const VIEW_APPROVAL = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_APPROVAL,
  );

  const {
    actions: {setPageTitleProps},
  } = usePageTitle();
  const router = useHistory();
  const {location} = router;
  const position = location.query.position;
  const approvalRef = React.useRef(null);

  const [selectedDateRangeOption, setSelectedDateRangeOption] =
    React.useState('thirtyDays');
  const [refetch, setRefetch] = React.useState(false);
  const dispatch = useDispatch();
  const interviewSchedulesData = useSelector(selectCandidateInterviewSchedules);
  const hmExperienceSurveysData = useSelector(
    selectCandidateHmExperienceSurveys,
  );
  const completedInterviewSchedulesData = useSelector(
    selectCompletedInterviewSchedules,
  );

  const candidateFeedbacksData = useSelector(selectCandidateFeedbacks);
  const selectedFilters = useSelector(selectJobFilters);
  const isDependencyLoading = useAsyncDependency(
    () =>
      Promise.all([
        dispatch(getBaseInfo()),
        dispatch(getCandidateFeedbacks('PENDING')),
        dispatch(getHmExperienceSurveys('PENDING')),
        dispatch(getInterviewSchedules('PENDING')),
        dispatch(getInterviewSchedules('COMPLETED')),
        dispatch(getSavedDashboards()),
      ]),
    [],
  );

  const {
    // @ts-expect-error - TS2339 - Property 'job_status' does not exist on type 'HiringPipelineFilters'.
    job_status,
    job_id,
    job_department,
    job_location,
    job_source,
    job_recruiter,
  } = selectedFilters;
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);

  const selectedDateRange = getDateRangeDataByOption(
    selectedDateRangeOption,
    agencyTimezone,
  );

  const {startDateUTC, endDateUTC} = selectedDateRange;

  const commonPayload = React.useMemo(() => {
    return {
      job_id,
      job_department,
      job_location,
      job_source,
      job_recruiter,
      end_date: endDateUTC,
      start_date: startDateUTC,
    };
  }, [
    job_status,
    job_id,
    job_department,
    job_location,
    job_source,
    job_recruiter,
    startDateUTC,
    endDateUTC,
  ]);
  const {result: tasksData = [], isLoading: isTasksDataLoading} = useApi(
    `/api/crm-dashboard/pending-tasks`,
    {
      apiPath: '/trmapi',
      data: {
        jobUserRoles: [],
        dateRange: null,
        teamIds: [],
        userIds: [],
        departmentId: 0,
        officeId: 0,
        assignedToMe: true,
      },
      method: 'POST',
      deps: [],
    },
  );

  const {result: jobsAssingedToMe, isLoading: isJobsLoading} = useApi(
    `/api/jobs/v2/overview`,
    {
      apiPath: '/trmapi',
      data: {
        teamIds: [],
        userIds: [],
        departmentId: 0,
        officeId: 0,
        dateRange: null,
        jobUserRoles: [],
        assignedToMe: true,
      },
      method: 'POST',
      deps: [],
    },
  );

  const {result: overviewData, isLoading: isOverviewDataLoading} = useApi(
    `/v1/analytics/crm/hiring-pipeline/pipeline-velocity/overview`,
    {
      apiPath: '/api',
      data: {
        ...commonPayload,
      },
      method: 'POST',
      deps: [],
    },
  );
  const {
    result: pendingApprovalsData = [],
    isLoading: isPendingApprovalsDataLoading,
  } = useApi(`/api/approval-status/PENDING`, {
    apiPath: '/trmapi',
    deps: [refetch],
  });

  const {
    result: rejectedApprovalsData = [],
    isLoading: isRjectedApprovalsDataLoading,
  } = useApi(`/api/approval-status/REJECTED`, {
    apiPath: '/trmapi',
    deps: [refetch],
  });
  const {
    result: approvedApprovalsData = [],
    isLoading: iaApprovedApprovalsDataLoading,
  } = useApi(`/api/approval-status/APPROVED`, {
    apiPath: '/trmapi',
    deps: [refetch],
  });

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  React.useEffect(() => {
    if (position === 'approvals') {
      // Use setTimeout to ensure the component is mounted and ref is available
      if (
        isDependencyLoading ||
        isJobsLoading ||
        isTasksDataLoading ||
        isOverviewDataLoading
      ) {
        return;
      }
      const timer = setTimeout(() => {
        if (approvalRef.current) {
          // @ts-expect-error - TS2339 - Property 'scrollIntoView' does not exist on type 'never'.
          approvalRef.current.scrollIntoView({behavior: 'smooth'});
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    isDependencyLoading,
    isJobsLoading,
    isTasksDataLoading,
    isOverviewDataLoading,
    position,
  ]);

  const [upcomingInterviewData, recentInterviewData] = React.useMemo(() => {
    // @ts-expect-error TS2304
    const upcoming: Array<InterviewData> = [];
    const recent: Array<any> = [];

    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
    if (interviewSchedulesData?.content) {
      const currentDateTime = new Date();

      // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'. | TS7006 - Parameter 'interview' implicitly has an 'any' type.
      interviewSchedulesData.content.forEach((interview) => {
        const endDateTime = new Date(interview.endDate);

        if (endDateTime > currentDateTime) {
          upcoming.push(interview);
        }

        if (endDateTime <= currentDateTime) {
          recent.push(interview);
        }
      });
    }

    return [upcoming, recent];
  }, [interviewSchedulesData]);

  React.useEffect(() => {
    setPageTitleProps({
      showBackButton: false,
      tabSlotComponent: null,
      rightSlotComponent: null,
      customPageNameComponent: (
        <PageName>
          <TitleMedium>{t('Dashboard', 'Dashboard')}</TitleMedium>
          <Icon type="duotone" name="house" style={duotoneIconStyle} />
        </PageName>
      ),
    });
  }, [language]);

  if (
    isDependencyLoading ||
    isJobsLoading ||
    isTasksDataLoading ||
    isOverviewDataLoading
  ) {
    return <CircularLoader className={css.loader} size="large" />;
  }

  // @ts-expect-error - TS2339 - Property 'openRequisition' does not exist on type '{}'. | TS2339 - Property 'openPosition' does not exist on type '{}'.
  const {openRequisition, openPosition} = jobsAssingedToMe || {};
  // @ts-expect-error - TS2339 - Property 'time_to_hire' does not exist on type '{}'. | TS2339 - Property 'time_to_submit' does not exist on type '{}'.
  const {time_to_hire, time_to_submit} = overviewData || {};
  const jobsMiddleContent = `${openRequisition || 0} ${t(
    'Open_Jobs',
    'Open Jobs',
  )}`;
  const jobsBottomContent = `${openPosition || 0} ${t(
    'Positions',
    'Positions',
  )}`;

  const timeToHireBottomContent = t(
    'Based_on_the_past_month',
    'Based on the past month',
  );
  const timeToHireMiddleContent = `${secondsToTimeBreakdown(
    (time_to_hire || 0) * 3600,
  )}`;

  const timeToSubmitBottomContent = t(
    'Based_on_the_past_month',
    'Based on the past month',
  );
  const timeToSubmitMiddleContent = `${secondsToTimeBreakdown(
    (time_to_submit || 0) * 3600,
  )}`;

  const VIEW_HELPFUL_LINKS =
    DOWNLOAD_SURVEY_RESPONSE || INTERNAL_JOB_POSTING || VIEW_RESUME_MAILBOX;

  const getApprovalsArray = (data: null | Array<never>) => [
    // @ts-expect-error - TS2339 - Property 'jobApproverStatuses' does not exist on type 'never[]'.
    ...(data?.jobApproverStatuses || []),
    // @ts-expect-error - TS2339 - Property 'offerLetterApproverStatuses' does not exist on type 'never[]'.
    ...(data?.offerLetterApproverStatuses || []),
  ];

  // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'never[] | null'.
  const approvedApprovals = getApprovalsArray(approvedApprovalsData);
  // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'never[] | null'.
  const rejectedApprovals = getApprovalsArray(rejectedApprovalsData);
  // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'never[] | null'.
  const pendingApprovals = getApprovalsArray(pendingApprovalsData);
  const handleMyJobs = () => {
    router.push('/crm/jobs/hiring');
  };
  const handleRedirectToAnalytics = () => {
    router.push('/reports/hiring-pipeline?fromAnalytics=true');
  };
  const kpiData = [
    {
      iconName: 'briefcase',
      label: t('Jobs', 'Jobs'),
      middleContent: jobsMiddleContent,
      bottomContent: jobsBottomContent,
      onClick: handleMyJobs,
    },
    {
      iconName: 'clock',
      label: t('Time_to_Hire', 'Time to Hire'),
      middleContent: timeToHireMiddleContent,
      bottomContent: timeToHireBottomContent,
      onClick: handleRedirectToAnalytics,
    },
    {
      iconName: 'reply-clock',
      label: t('Time_to_Submit', 'Time to Submit'),
      middleContent: timeToSubmitMiddleContent,
      bottomContent: timeToSubmitBottomContent,
      onClick: handleRedirectToAnalytics,
    },
  ];

  return (
    <div className={css.dashboardContainer}>
      <div className={css.headerWrapper}>
        {GLOBAL_DASHBOARD_STATS && (
          <>
            {kpiData.map((kpi) => (
              <DashboardKPI
                key={kpi.label}
                iconName={kpi.iconName}
                label={kpi.label}
                middleContent={kpi.middleContent}
                bottomContent={kpi.bottomContent}
                onClick={kpi.onClick}
              />
            ))}
          </>
        )}
      </div>
      <div className={css.dashboardWrapper}>
        <div className={css.leftContainer}>
          {GLOBAL_DASHBOARD_STATS && <HiringPipelinePerformanceChart />}
          <UpcomingInterview
            interviewData={upcomingInterviewData}
            isInterviewsLoading={isDependencyLoading}
            from={'DASHBOARD'}
          />
          <Feedbacks isLoading={isDependencyLoading} from={'DASHBOARD'} />
          {VIEW_APPROVAL ? (
            <div ref={approvalRef} className={css.approvalContainer}>
              <PendingApprovals
                approvedApprovals={approvedApprovals}
                rejectedApprovals={rejectedApprovals}
                pendingApprovals={pendingApprovals}
                onSubmit={() => setRefetch(!refetch)}
                isApprovalsLoading={isPendingApprovalsDataLoading}
              />
            </div>
          ) : null}
        </div>
        <div className={css.rightContainer}>
          {ALL_TASK_STATS && (
            <>
              <Tasks
                // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'TaskData[] | null'.
                tasksData={tasksData}
                isLoading={isTasksDataLoading}
                isDashBoardPage={true}
              />
            </>
          )}
          {(ADD_REFERRALS || SHARE_REFERRAL_LINK) && <Referrals />}
          {VIEW_HELPFUL_LINKS && <HelpfulLinks />}
        </div>
      </div>
    </div>
  );
};

type DashboardKPIProps = {
  iconName: string;
  label: string;
  middleContent: string | React.ReactNode;
  bottomContent: string | React.ReactNode;
  wrapperClassName?: string;
  onClick?: () => void;
};

const DashboardKPI = ({
  iconName,
  label,
  middleContent,
  bottomContent,
  onClick,
}: DashboardKPIProps) => {
  return (
    <KPIBox
      bottomContent={bottomContent}
      iconName={iconName}
      semantic="neutral"
      middleContent={middleContent}
      topContent={label}
      classNames={{wrapper: css.kpiWrapper}}
      // @ts-expect-error - TS2769 - No overload matches this call.
      onClick={onClick}
    />
  );
};
